import { conditionalRender } from '@repo/toolkit/conditional-render'
import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import { getJourneys } from '../../../api/products/get-journeys'
import { getTrips } from '../../../api/trips/get-trips'
import {
  ApplicationStatusEnum,
  applicationStatusEnumMapping
} from '../../../lib/types/enums/enrollment/application-status.enum'
import { EnrollmentInformationStatus } from '../../../lib/types/enums/enrollment/enrollment-information-status.enum'
import {
  MedicalConditionEnum,
  medicalConditionEnumMapping
} from '../../../lib/types/enums/enrollment/medical-condition-enum'
import {
  AccomodationCategoryEnum,
  accomodationCategoryEnumMapping
} from '../../../lib/types/enums/product/accomodation-category.enum'
import {
  AcademicZoneEnum,
  academicZoneEnumMapping
} from '../../../lib/types/enums/staff/academic-zone.enum'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../elements/country-flag'

interface Props {
  filter?: EnrollmentFilter
  onFilterChange: (filter: EnrollmentFilter) => void
}

export function EnrollmentsCenterFilter({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof EnrollmentFilter>(key: K, value: EnrollmentFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: journeys } = useQuery({
    queryKey: ['journeys'],
    queryFn: () => getJourneys()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  if (!filter) return null

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <SelectInput
        label={'Pays'}
        options={countries?.data.map((country) => ({
          label: country.name,
          value: country.code
        }))}
        selectedOption={conditionalRender(!!filter.country, {
          value: filter.country,
          label: filter.country
            ? (countries?.data.find((country) => country.code === filter.country)?.name ?? '')
            : ''
        })}
        onChange={(value) => setFilter('country', value)}
      />

      <MultiSelectInput
        searchable
        label={'Période(s)'}
        variant={'warning'}
        options={periods?.data.map((period) => ({
          label: period.name,
          value: period.name
        }))}
        selectedOptions={filter.periods?.map((period) => ({
          label: period,
          value: period
        }))}
        onChange={(periods) => setFilter('periods', periods)}
      />

      <MultiSelectInput
        searchable
        label={'Formules'}
        variant={'primary'}
        options={formulas?.data.map((formula) => ({
          label: getTranslatedName(formula),
          value: getTranslatedName(formula)
        }))}
        selectedOptions={formulas?.data
          .filter((formula) => filter.formulas?.includes(getTranslatedName(formula)))
          .map((formula) => ({
            label: getTranslatedName(formula),
            value: getTranslatedName(formula)
          }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
      />

      <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1 tablet:grid-cols-2">
        <DateStringInput
          mode={'over'}
          label={'Départ après le'}
          value={filter?.departureDateAfter}
          onChange={(date) => setFilter('departureDateAfter', date ?? undefined)}
        />
        <DateStringInput
          mode={'over'}
          label={'Départ avant le'}
          value={filter?.departureDateBefore}
          onChange={(date) => setFilter('departureDateBefore', date ?? undefined)}
        />
        <DateStringInput
          mode={'over'}
          label={'Retour après le'}
          value={filter?.returnDateAfter}
          onChange={(date) => setFilter('returnDateAfter', date ?? undefined)}
        />
        <DateStringInput
          mode={'over'}
          label={'Retour avant le'}
          value={filter?.returnDateBefore}
          onChange={(date) => setFilter('returnDateBefore', date ?? undefined)}
        />
      </div>

      <MultiSelectInput
        label={"Type d'hébergement"}
        variant={'primary'}
        searchable
        options={Object.values(AccomodationCategoryEnum).map((option) => ({
          label: accomodationCategoryEnumMapping[option],
          value: option
        }))}
        selectedOptions={filter?.accomodations?.map((accommodation) => ({
          label: accomodationCategoryEnumMapping[accommodation],
          value: accommodation
        }))}
        onChange={(accomodations) => setFilter('accomodations', accomodations)}
      />

      <SelectInput
        label={'Voyage principal'}
        searchable
        options={trips?.data.map((trip) => ({
          label: TripProperties.getTripLabel(trip),
          value: trip.id
        }))}
        onChange={(tripId) => setFilter('mainTripId', tripId)}
        selectedOption={conditionalRender(!!filter.mainTripId, {
          label: TripProperties.getTripLabel(
            trips?.data.find((trip) => trip.id === filter.mainTripId)
          ),
          value: filter.mainTripId
        })}
      />

      <SelectInput
        label={'Voyage secondaire'}
        searchable
        options={trips?.data.map((trip) => ({
          label: TripProperties.getTripLabel(trip),
          value: trip.id
        }))}
        onChange={(tripId) => setFilter('secondaryTripId', tripId)}
        selectedOption={conditionalRender(!!filter.secondaryTripId, {
          label: TripProperties.getTripLabel(
            trips?.data.find((trip) => trip.id === filter.secondaryTripId)
          ),
          value: filter.secondaryTripId
        })}
      />

      <SelectInput
        label={'Niveau de santé'}
        options={Object.values(MedicalConditionEnum).map((value) => ({
          label: medicalConditionEnumMapping[value].label,
          value
        }))}
        selectedOption={conditionalRender(!!filter.medicalLevel, {
          label: filter.medicalLevel ? medicalConditionEnumMapping[filter.medicalLevel].label : '',
          value: filter.medicalLevel
        })}
        onChange={(medicalLevel) => setFilter('medicalLevel', medicalLevel)}
      />

      <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1 tablet:grid-cols-2">
        <DateStringInput
          mode={'over'}
          label={'Confirmé après le'}
          value={filter?.confirmedAfter}
          onChange={(date) => setFilter('confirmedAfter', date ?? undefined)}
        />
        <DateStringInput
          mode={'over'}
          label={'Confirmé avant le'}
          value={filter?.confirmedBefore}
          onChange={(date) => setFilter('confirmedBefore', date ?? undefined)}
        />
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Statut</Typography>

        <div className="flex flex-row gap-5 flex-wrap">
          <Checkbox
            label={applicationStatusEnumMapping[ApplicationStatusEnum.CONFIRMED].label}
            checked={filter?.statuses?.includes(ApplicationStatusEnum.CONFIRMED)}
            onChange={(checked) => {
              const statuses = filter?.statuses ?? []
              if (checked) {
                setFilter('statuses', [...statuses, ApplicationStatusEnum.CONFIRMED])
              } else {
                setFilter(
                  'statuses',
                  statuses.filter((s) => s !== ApplicationStatusEnum.CONFIRMED)
                )
              }
            }}
          />

          <Checkbox
            label={applicationStatusEnumMapping[ApplicationStatusEnum.AFFECTED].label}
            checked={filter?.statuses?.includes(ApplicationStatusEnum.AFFECTED)}
            onChange={(checked) => {
              const statuses = filter?.statuses ?? []
              if (checked) {
                setFilter('statuses', [...statuses, ApplicationStatusEnum.AFFECTED])
              } else {
                setFilter(
                  'statuses',
                  statuses.filter((s) => s !== ApplicationStatusEnum.AFFECTED)
                )
              }
            }}
          />

          <Checkbox
            label={
              applicationStatusEnumMapping[ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION]
                .label
            }
            checked={filter?.statuses?.includes(
              ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION
            )}
            onChange={(checked) => {
              const statuses = filter?.statuses ?? []
              if (checked) {
                setFilter('statuses', [
                  ...statuses,
                  ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION
                ])
              } else {
                setFilter(
                  'statuses',
                  statuses.filter((s) => s !== ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION)
                )
              }
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1 tablet:grid-cols-2">
        <DateStringInput
          mode={'over'}
          label={'Affecté après le'}
          value={filter?.assignedAfter}
          onChange={(date) => setFilter('assignedAfter', date ?? undefined)}
        />
        <DateStringInput
          mode={'over'}
          label={'Affecté avant le'}
          value={filter?.assignedBefore}
          onChange={(date) => setFilter('assignedBefore', date ?? undefined)}
        />
      </div>

      <SelectInput
        label={'Zone academique'}
        options={Object.values(AcademicZoneEnum)
          .filter((a) => a !== AcademicZoneEnum.NONE)
          .map((academicZone) => ({
            label: academicZoneEnumMapping[academicZone],
            value: academicZone
          }))}
        selectedOption={conditionalRender(!!filter.academicZone, {
          value: filter.academicZone,
          label: filter.academicZone ? academicZoneEnumMapping[filter.academicZone] : ''
        })}
        onChange={(value) => setFilter('academicZone', value)}
      />

      <SelectInput
        label={'Séjour'}
        searchable
        options={journeys?.data.map((journey) => ({
          label: journey.reference,
          value: journey.reference,
          custom: (
            <CountryFlag code={journey.date.product.country.code} label={journey.reference} />
          )
        }))}
        selectedOption={conditionalRender(!!filter.journeyReference, {
          value: filter.journeyReference,
          label:
            journeys?.data.find((journey) => journey.reference === filter.journeyReference)
              ?.reference ?? ''
        })}
        onChange={(value) => setFilter('journeyReference', value)}
      />

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>AA</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.isAA === undefined}
            onCheck={() => setFilter('isAA', undefined)}
          />
          <RadioInput
            label={'AA'}
            checked={filter?.isAA === true}
            onCheck={() => setFilter('isAA', true)}
          />

          <RadioInput
            label={'Pas AA'}
            checked={filter?.isAA === false}
            onCheck={() => setFilter('isAA', false)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Paiement</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.isPaymentFullyPaid === undefined}
            onCheck={() => setFilter('isPaymentFullyPaid', undefined)}
          />

          <RadioInput
            label={'Pas entièrement payé'}
            checked={filter?.isPaymentFullyPaid === false}
            onCheck={() => setFilter('isPaymentFullyPaid', false)}
          />

          <RadioInput
            label={'Entièrement payé'}
            checked={filter?.isPaymentFullyPaid === true}
            onCheck={() => setFilter('isPaymentFullyPaid', true)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Documents administratifs</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.documentsCompleted === undefined}
            onCheck={() => setFilter('documentsCompleted', undefined)}
          />

          <RadioInput
            label={'Tous complétés'}
            checked={filter?.documentsCompleted === true}
            onCheck={() => setFilter('documentsCompleted', true)}
          />

          <RadioInput
            label={'Non complétés'}
            checked={filter?.documentsCompleted === false}
            onCheck={() => setFilter('documentsCompleted', false)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Profil de mon enfant</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Non complété'}
            checked={filter?.enrollmentProfileStatus === EnrollmentInformationStatus.PENDING}
            onCheck={() =>
              setFilter('enrollmentProfileStatus', EnrollmentInformationStatus.PENDING)
            }
          />

          <RadioInput
            label={'À valider'}
            checked={
              filter?.enrollmentProfileStatus === EnrollmentInformationStatus.TO_BE_COMPLETED
            }
            onCheck={() =>
              setFilter('enrollmentProfileStatus', EnrollmentInformationStatus.TO_BE_COMPLETED)
            }
          />

          <RadioInput
            label={'Validé'}
            checked={filter?.enrollmentProfileStatus === EnrollmentInformationStatus.COMPLETED}
            onCheck={() =>
              setFilter('enrollmentProfileStatus', EnrollmentInformationStatus.COMPLETED)
            }
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Papiers d'identité</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.isIdentityDocumentsComplete === undefined}
            onCheck={() => setFilter('isIdentityDocumentsComplete', undefined)}
          />

          <RadioInput
            label={'Non complété'}
            checked={filter?.isIdentityDocumentsComplete === false}
            onCheck={() => setFilter('isIdentityDocumentsComplete', false)}
          />

          <RadioInput
            label={'Complété'}
            checked={filter?.isIdentityDocumentsComplete === true}
            onCheck={() => setFilter('isIdentityDocumentsComplete', true)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Fiche sanitaire de liaison</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.isHealthDocumentsComplete === undefined}
            onCheck={() => setFilter('isHealthDocumentsComplete', undefined)}
          />

          <RadioInput
            label={'Non complété'}
            checked={filter?.isHealthDocumentsComplete === false}
            onCheck={() => setFilter('isHealthDocumentsComplete', false)}
          />

          <RadioInput
            label={'Complété'}
            checked={filter?.isHealthDocumentsComplete === true}
            onCheck={() => setFilter('isHealthDocumentsComplete', true)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Test de niveau</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.isTestComplete === undefined}
            onCheck={() => setFilter('isTestComplete', undefined)}
          />

          <RadioInput
            label={'Non complété'}
            checked={filter?.isTestComplete === false}
            onCheck={() => setFilter('isTestComplete', false)}
          />

          <RadioInput
            label={'Complété'}
            checked={filter?.isTestComplete === true}
            onCheck={() => setFilter('isTestComplete', true)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Informations sur le voyage individuel</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.isIndividualTravelInfoComplete === undefined}
            onCheck={() => setFilter('isIndividualTravelInfoComplete', undefined)}
          />

          <RadioInput
            label={'Non complété'}
            checked={filter?.isIndividualTravelInfoComplete === false}
            onCheck={() => setFilter('isIndividualTravelInfoComplete', false)}
          />

          <RadioInput
            label={'Complété'}
            checked={filter?.isIndividualTravelInfoComplete === true}
            onCheck={() => setFilter('isIndividualTravelInfoComplete', true)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Placement en famille</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'Tous'}
            checked={filter?.familyPlacements === undefined}
            onCheck={() => setFilter('familyPlacements', undefined)}
          />
          <RadioInput
            label={'Placé en famille'}
            checked={filter?.familyPlacements === true}
            onCheck={() => setFilter('familyPlacements', true)}
          />

          <RadioInput
            label={'Non placé en famille'}
            checked={filter?.familyPlacements === false}
            onCheck={() => setFilter('familyPlacements', false)}
          />
        </div>
      </div>
    </div>
  )
}
